// src/services/consent.js
//
// P0-5 (Profil Derin Analiz V2) — KVKK açık rıza kaydı (client tarafı).
//
// Akış:
//   1. useConsentGate (App.js auth-ready) hasCurrentConsent() ile
//      users/{uid}/consents/{CURRENT_CONSENT_VERSION} doc'unun varlığını
//      kontrol eder (rules: read owner-only).
//   2. Yoksa ConsentSheet açılır; kullanıcı Gizlilik Politikası +
//      Kullanım Koşulları onayını verir.
//   3. recordConsent() → recordConsentV3 callable'ı: acceptedAt
//      serverTimestamp + IP-hash + userAgent ile immutable kayıt yazar
//      (client'ın consents subcollection'a write yolu yok).
//
// Sürüm politikası: yasal metinler değiştiğinde CURRENT_CONSENT_VERSION
// artırılır → tüm kullanıcılardan yeni sürüm için tekrar onay istenir.
// Format server'daki VERSION_PATTERN ile uyumlu olmalı: v{yıl}-{ay}[ek].

import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const REGION = "europe-west3";

export const CURRENT_CONSENT_VERSION = "v2026-06";

/**
 * Geçerli sürüm için rıza kaydı var mı?
 * Dönüş sözleşmesi:
 *   true  → kayıt var (sheet açılmaz)
 *   false → kayıt yok (sheet açılır)
 *   null  → belirlenemedi (offline/permission) — caller fail-open
 *           davranmalı: uygulamayı KİLİTLEME, cache de yazma; bir sonraki
 *           oturumda tekrar denenir.
 */
// Gövde `if (!uid) return null` ile başlıyor → boş değer beklenen bir girdi.
export async function hasCurrentConsent(uid: string | null | undefined) {
  if (!uid) return null;
  try {
    const snap = await getDoc(
      doc(db, "users", uid, "consents", CURRENT_CONSENT_VERSION)
    );
    return snap.exists();
  } catch {
    return null;
  }
}

/**
 * Rıza kaydını server'a yazdırır (recordConsentV3 callable).
 * Her iki onay da true olmalı; callable aksi halde failed-precondition döner.
 *
 * @returns {Promise<{ok: true, alreadyRecorded: boolean}>}
 */
export async function recordConsent() {
  if (!auth.currentUser) throw new Error("AUTH_REQUIRED");
  const callable = httpsCallable(getFunctions(undefined, REGION), "recordConsentV3");
  const res = await callable({
    version: CURRENT_CONSENT_VERSION,
    privacyAccepted: true,
    termsAccepted: true,
  });
  return res?.data || { ok: true, alreadyRecorded: false };
}
